import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, TrendingUp, ShoppingCart, ArrowRight, Zap, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, txnRes, stripeRes] = await Promise.all([
          api.get('/analytics/dashboard?period=1d'),
          api.get('/transactions?limit=5'),
          api.get('/stripe/status')
        ]);
        setAnalytics(analyticsRes.data);
        setTransactions(txnRes.data.transactions);
        setStripeStatus(stripeRes.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusClass = (status) => {
    const map = {
      paid: 'status-paid',
      initiated: 'status-pending',
      pending: 'status-pending',
      failed: 'status-failed',
      expired: 'status-failed',
      refunded: 'status-refunded'
    };
    return map[status] || 'status-pending';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-28 bg-slate-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Today's overview</p>
        </div>
        <Button
          onClick={() => navigate('/payment')}
          className="h-12 px-6 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide"
          data-testid="quick-payment-btn"
        >
          <Zap className="w-4 h-4 mr-2" />
          New Payment
        </Button>
      </div>

      {/* Stripe Connection Status */}
      {stripeStatus && !stripeStatus.connected && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 p-5" data-testid="stripe-warning-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Action required: Connect your Stripe account</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">You must connect your Stripe account to start accepting payments. Customers pay directly to your bank account — EmergencyPOS never holds your funds.</p>
            </div>
            <Button onClick={() => navigate('/settings')} className="bg-[#635BFF] hover:bg-[#5851DB] text-white text-sm font-semibold h-10 px-4 flex-shrink-0" data-testid="connect-stripe-dashboard-btn">
              <CreditCard className="w-4 h-4 mr-2" /> Connect Stripe
            </Button>
          </div>
        </div>
      )}

      {/* Quick Payment Buttons */}
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 ${stripeStatus && !stripeStatus.connected ? 'opacity-40 pointer-events-none' : ''}`} data-testid="quick-payment-grid">
        {[5, 10, 20, 50].map(val => (
          <button
            key={val}
            onClick={() => navigate('/payment', { state: { amount: val } })}
            className="bg-card border border-border p-4 text-center hover:border-orange-500/50 transition-colors group"
            data-testid={`dashboard-quick-${val}`}
          >
            <div className="text-2xl font-extrabold text-foreground group-hover:text-orange-500 transition-colors" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{val} EUR</div>
            <div className="text-xs text-muted-foreground mt-1">Quick charge</div>
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border" data-testid="stats-grid">
        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Revenue Today</p>
                <p className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="revenue-today">
                  {formatCurrency(analytics?.total_revenue || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Transactions</p>
                <p className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="transaction-count">
                  {analytics?.paid_transactions || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none rounded-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Avg Basket</p>
                <p className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="avg-basket">
                  {formatCurrency(analytics?.avg_basket || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-50 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recent Transactions
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/transactions')}
            className="text-slate-500 hover:text-slate-900 font-medium"
            data-testid="view-all-transactions-btn"
          >
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {transactions.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No transactions yet. Create your first payment!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-card border border-border" data-testid="recent-transactions-list">
            {transactions.map((txn, i) => (
              <div
                key={txn.transaction_id}
                className={`flex items-center justify-between px-4 py-3 ${i < transactions.length - 1 ? 'border-b border-slate-100' : ''}`}
                data-testid={`transaction-row-${txn.transaction_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {txn.description || `Payment ${txn.transaction_id.slice(-6)}`}
                    </span>
                    <Badge variant="outline" className={`text-xs px-2 py-0 ${getStatusClass(txn.payment_status)}`}>
                      {txn.payment_status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {formatTime(txn.created_at)} · {txn.created_by_name || 'Staff'}
                  </p>
                </div>
                <span className="text-sm font-bold ml-4 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatCurrency(txn.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
